import { useState, useCallback, useRef } from 'react'
import type { Control, UseFormRegister, UseFormSetValue, FieldErrors } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { Upload, X, Loader2, Plus } from 'lucide-react'
import { MagicInput as Input, MagicTextarea as Textarea } from '@/components/ui/magic-input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Combobox } from '@/components/ui/combobox'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { MarkdownPreview } from '@/features/projectBoards/components/MarkdownField'
import type { ApplicationSection, ApplicationField } from '@/types'
import { formatApplicationSubmissionError, withErrorCode } from '@/lib/errors'

import { validateNumberAnswer } from '../lib/numberValidation'

// The dynamic form uses a flat key-value record, keyed by field.id
export type DynamicFormValues = {
  applicantInfo: { firstName: string; lastName: string; email: string }
  answers: Record<string, string | string[] | number | boolean | null>
  files: Record<string, string | null>
}

interface FileUploadState {
  file: File | null
  progress: number
  uploading: boolean
  url: string | null
  error: string | null
}

function makeFileState(patch: Partial<FileUploadState>, base?: FileUploadState): FileUploadState {
  return {
    file: base?.file ?? null,
    progress: base?.progress ?? 0,
    uploading: base?.uploading ?? false,
    url: base?.url ?? null,
    error: base?.error ?? null,
    ...patch,
  }
}

interface Props {
  sections: ApplicationSection[]
  register: UseFormRegister<DynamicFormValues>
  control: Control<DynamicFormValues>
  setValue: UseFormSetValue<DynamicFormValues>
  errors: FieldErrors<DynamicFormValues>
  onFileUpload?: (fieldId: string, file: File, onProgress: (pct: number) => void) => Promise<string>
  onFileUrlReady?: (fieldId: string, url: string) => void
  onFileRemove?: (fieldId: string) => void
  onFileStateChange?: (fieldId: string, uploading: boolean) => void
  disabled?: boolean
}

export function DynamicFormRenderer({
  sections,
  register,
  control,
  setValue,
  errors,
  onFileUpload,
  onFileUrlReady,
  onFileRemove,
  onFileStateChange,
  disabled,
}: Props) {
  const [fileStates, setFileStates] = useState<Record<string, FileUploadState>>({})

  const handleFileChange = useCallback(
    async (field: ApplicationField, file: File) => {
      if (!onFileUpload) return
      const fieldId = field.id
      const maxBytes = (field.maxFileSizeMB ?? 10) * 1024 * 1024
      if (file.size < 1 || file.size > maxBytes) {
        const error = withErrorCode(
          `${field.label} must be ${field.maxFileSizeMB ?? 10} MB or smaller.`,
          'applications/validation'
        )
        setFileStates((prev) => ({
          ...prev,
          [fieldId]: makeFileState({ file, uploading: false, url: null, error }),
        }))
        setValue(`files.${fieldId}`, null, { shouldValidate: true })
        onFileStateChange?.(fieldId, false)
        return
      }
      setFileStates((prev) => ({
        ...prev,
        [fieldId]: makeFileState({ file, progress: 0, uploading: true, url: null, error: null }),
      }))
      setValue(`files.${fieldId}`, null, { shouldValidate: false })
      onFileStateChange?.(fieldId, true)
      try {
        const url = await onFileUpload(fieldId, file, (pct) => {
          setFileStates((prev) => ({
            ...prev,
            [fieldId]: makeFileState({ progress: pct }, prev[fieldId]),
          }))
        })
        setFileStates((prev) => ({
          ...prev,
          [fieldId]: makeFileState({ file, progress: 100, uploading: false, url, error: null }),
        }))
        setValue(`files.${fieldId}`, url, { shouldValidate: true })
        onFileUrlReady?.(fieldId, url)
      } catch (error) {
        setFileStates((prev) => ({
          ...prev,
          [fieldId]: makeFileState(
            {
              uploading: false,
              url: null,
              error: formatApplicationSubmissionError(
                error,
                'We could not upload this file. Please try again.'
              ),
            },
            prev[fieldId]
          ),
        }))
        setValue(`files.${fieldId}`, null, { shouldValidate: true })
      } finally {
        onFileStateChange?.(fieldId, false)
      }
    },
    [onFileStateChange, onFileUpload, onFileUrlReady, setValue]
  )

  const handleFileRemove = useCallback(
    (fieldId: string) => {
      setFileStates((prev) => {
        const next = { ...prev }
        delete next[fieldId]
        return next
      })
      setValue(`files.${fieldId}`, null, { shouldValidate: true })
      onFileRemove?.(fieldId)
      onFileStateChange?.(fieldId, false)
    },
    [onFileRemove, onFileStateChange, setValue]
  )

  const sortedSections = [...sections].sort((a, b) => a.order - b.order)

  return (
    <div className="space-y-6">
      {sortedSections.map((section) => {
        const sortedFields = [...section.fields].sort((a, b) => a.order - b.order)
        return (
          <Card key={section.id}>
            <CardHeader>
              <CardTitle className="text-base">{section.title}</CardTitle>
              {section.description && <CardDescription>{section.description}</CardDescription>}
            </CardHeader>
            <CardContent className="space-y-5">
              {sortedFields.map((field) => (
                <FieldRenderer
                  key={field.id}
                  field={field}
                  register={register}
                  control={control}
                  errors={errors}
                  fileState={fileStates[field.id]}
                  onFileChange={handleFileChange}
                  onFileRemove={handleFileRemove}
                  disabled={disabled}
                />
              ))}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

interface TextListInputProps {
  id: string
  value: string[]
  onChange: (v: string[]) => void
  placeholder?: string
  maxEntries?: number
  disabled?: boolean
}

function TextListInput({
  id,
  value,
  onChange,
  placeholder,
  maxEntries,
  disabled,
}: TextListInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const atCap = maxEntries != null && value.length >= maxEntries

  function addEntry() {
    const val = inputRef.current?.value.trim()
    if (!val || atCap) return
    onChange([...value, val])
    if (inputRef.current) inputRef.current.value = ''
  }

  function removeEntry(i: number) {
    onChange(value.filter((_, idx) => idx !== i))
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          id={id}
          placeholder={placeholder ?? 'Type an entry…'}
          maxLength={500}
          disabled={disabled || atCap}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addEntry()
            }
          }}
        />
        <button
          type="button"
          disabled={disabled || atCap}
          onClick={addEntry}
          className="flex shrink-0 items-center gap-1 rounded-md border px-3 py-2 text-sm transition-colors hover:bg-accent disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>
      {maxEntries != null && (
        <p className="text-xs text-muted-foreground">
          {value.length} / {maxEntries} entries
        </p>
      )}
      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((entry, i) => (
            <li
              key={i}
              className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-1.5 text-sm"
            >
              <span className="flex-1">{entry}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => removeEntry(i)}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

interface FieldRendererProps {
  field: ApplicationField
  register: UseFormRegister<DynamicFormValues>
  control: Control<DynamicFormValues>
  errors: FieldErrors<DynamicFormValues>
  fileState?: FileUploadState
  onFileChange: (field: ApplicationField, file: File) => Promise<void>
  onFileRemove: (fieldId: string) => void
  disabled?: boolean
}

function FieldRenderer({
  field,
  register,
  control,
  errors,
  fileState,
  onFileChange,
  onFileRemove,
  disabled,
}: FieldRendererProps) {
  const fieldKey = `answers.${field.id}` as const
  const answerError = (errors.answers as Record<string, { message?: string } | undefined>)?.[
    field.id
  ]
  const fileError = (errors.files as Record<string, { message?: string } | undefined>)?.[field.id]
  const fieldError = field.type === 'file_upload' ? fileError : answerError

  const rules = field.required ? { required: `${field.label} is required` } : {}
  const labelNode = (
    <Label htmlFor={field.id}>
      {field.label}
      {field.required && <span className="ml-1 text-destructive">*</span>}
    </Label>
  )
  const descNode = field.description && (
    <div className="text-xs text-muted-foreground [&_a]:underline [&_a]:text-primary [&_p]:my-0">
      <MarkdownPreview source={field.description} transparent />
    </div>
  )
  const errorNode = fieldError && (
    <p className="text-sm text-destructive">
      {fieldError.message && withErrorCode(fieldError.message, 'app/validation')}
    </p>
  )

  switch (field.type) {
    case 'notice':
      return (
        <div className="rounded-md border border-muted bg-muted/30 px-4 py-3 text-sm [&_a]:underline [&_a]:text-primary">
          {field.label && <p className="mb-1 font-medium">{field.label}</p>}
          <MarkdownPreview source={field.description ?? ''} transparent />
        </div>
      )

    case 'short_text':
    case 'email':
    case 'phone':
    case 'url': {
      const urlRules =
        field.type === 'url'
          ? {
              ...rules,
              validate: (value: unknown) => {
                if (!value && !field.required) return true
                if (typeof value !== 'string' || value.length > 2048)
                  return `${field.label} must be a valid URL`
                try {
                  const url = new URL(value)
                  return (
                    url.protocol === 'http:' ||
                    url.protocol === 'https:' ||
                    `${field.label} must be a valid URL`
                  )
                } catch {
                  return `${field.label} must be a valid URL`
                }
              },
              setValueAs: (v: unknown) => {
                if (typeof v !== 'string') return v
                const trimmed = v.trim()
                if (!trimmed) return ''
                return /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed)
                  ? trimmed
                  : `https://${trimmed}`
              },
            }
          : field.type === 'email'
            ? {
                ...rules,
                maxLength: { value: 254, message: `${field.label} is too long` },
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: `${field.label} must be a valid email address`,
                },
              }
            : {
                ...rules,
                maxLength: { value: 500, message: `${field.label} is too long` },
              }
      return (
        <div className="space-y-1">
          {labelNode}
          {descNode}
          <Input
            id={field.id}
            type={field.type === 'email' ? 'email' : 'text'}
            inputMode={field.type === 'url' ? 'url' : undefined}
            placeholder={field.placeholder}
            disabled={disabled}
            {...register(fieldKey as `answers.${string}`, urlRules)}
          />
          {errorNode}
        </div>
      )
    }

    case 'long_text':
      return (
        <div className="space-y-1">
          {labelNode}
          {descNode}
          <Textarea
            id={field.id}
            rows={4}
            placeholder={field.placeholder}
            disabled={disabled}
            {...register(fieldKey as `answers.${string}`, {
              ...rules,
              maxLength: { value: 20_000, message: `${field.label} is too long` },
            })}
          />
          {errorNode}
        </div>
      )

    case 'number':
      return (
        <div className="space-y-1">
          {labelNode}
          {descNode}
          <Input
            id={field.id}
            type="number"
            min={field.min}
            max={field.max}
            step={field.integerOnly ? 1 : 'any'}
            placeholder={field.placeholder}
            disabled={disabled}
            {...register(fieldKey as `answers.${string}`, {
              ...rules,
              valueAsNumber: true,
              validate: (value) => {
                if (!field.required && (value === undefined || value === '' || value === null || Number.isNaN(value))) return true
                return validateNumberAnswer(Number(value), field, field.label)
              },
              ...(field.min !== undefined
                ? { min: { value: field.min, message: `${field.label} is too small` } }
                : {}),
              ...(field.max !== undefined
                ? { max: { value: field.max, message: `${field.label} is too large` } }
                : {}),
            })}
          />
          {errorNode}
        </div>
      )

    case 'date':
      return (
        <div className="space-y-1">
          {labelNode}
          {descNode}
          <Input
            id={field.id}
            type="date"
            disabled={disabled}
            {...register(fieldKey as `answers.${string}`, rules)}
          />
          {errorNode}
        </div>
      )

    case 'dropdown':
      return (
        <div className="space-y-1">
          {labelNode}
          {descNode}
          <Controller
            name={fieldKey as `answers.${string}`}
            control={control}
            rules={rules}
            render={({ field: f }) => (
              <Combobox
                value={(f.value as string) ?? ''}
                onChange={(value) => f.onChange(value)}
                disabled={disabled}
                inputId={field.id}
                placeholder="Select an option…"
                options={(field.options ?? []).map((option: string) => ({
                  label: option,
                  value: option,
                }))}
              />
            )}
          />
          {errorNode}
        </div>
      )

    case 'multiple_choice':
      return (
        <div className="space-y-2">
          {labelNode}
          {descNode}
          <Controller
            name={fieldKey as `answers.${string}`}
            control={control}
            rules={rules}
            render={({ field: f }) => (
              <div className="space-y-2">
                {(field.options ?? []).map((opt: string) => (
                  <label key={opt} className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      value={opt}
                      checked={f.value === opt}
                      onChange={() => f.onChange(opt)}
                      disabled={disabled}
                      className="accent-primary"
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            )}
          />
          {errorNode}
        </div>
      )

    case 'checkbox_group':
      return (
        <div className="space-y-2">
          {labelNode}
          {descNode}
          <Controller
            name={fieldKey as `answers.${string}`}
            control={control}
            rules={rules}
            render={({ field: f }) => {
              const selected: string[] = Array.isArray(f.value) ? (f.value as string[]) : []
              return (
                <div className="space-y-2">
                  {(field.options ?? []).map((opt: string) => (
                    <label key={opt} className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        checked={selected.includes(opt)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            f.onChange([...selected, opt])
                          } else {
                            f.onChange(selected.filter((s) => s !== opt))
                          }
                        }}
                        disabled={disabled}
                      />
                      <span className="text-sm">{opt}</span>
                    </label>
                  ))}
                </div>
              )
            }}
          />
          {errorNode}
        </div>
      )

    case 'linear_scale': {
      const min = field.min ?? 1
      const max = field.max ?? 5
      const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i)
      return (
        <div className="space-y-2">
          {labelNode}
          {descNode}
          <Controller
            name={fieldKey as `answers.${string}`}
            control={control}
            rules={rules}
            render={({ field: f }) => (
              <div className="space-y-2">
                <div className="flex justify-between px-1">
                  {steps.map((n) => (
                    <button
                      key={n}
                      type="button"
                      disabled={disabled}
                      onClick={() => f.onChange(n)}
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                        f.value === n
                          ? 'bg-primary text-primary-foreground'
                          : 'border hover:bg-accent'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                {(field.minLabel || field.maxLabel) && (
                  <div className="flex justify-between px-1 text-xs text-muted-foreground">
                    <span>{field.minLabel}</span>
                    <span>{field.maxLabel}</span>
                  </div>
                )}
              </div>
            )}
          />
          {errorNode}
        </div>
      )
    }

    case 'file_upload':
      return (
        <div className="space-y-1">
          {labelNode}
          {descNode}
          <input
            type="hidden"
            {...register(
              `files.${field.id}`,
              field.required ? { required: `${field.label} is required` } : {}
            )}
          />
          <div className="space-y-2">
            {fileState?.url ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm">{fileState.file?.name}</span>
                <button
                  type="button"
                  onClick={() => onFileRemove(field.id)}
                  disabled={disabled}
                  aria-label={`Remove ${fileState.file?.name ?? 'file'}`}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : fileState?.uploading ? (
              <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Uploading… {fileState.progress}%
                </span>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed px-3 py-3 transition-colors hover:bg-muted/30">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Click to upload
                  {field.maxFileSizeMB && ` (max ${field.maxFileSizeMB}MB)`}
                </span>
                <input
                  type="file"
                  className="hidden"
                  disabled={disabled}
                  accept={field.acceptedFileTypes?.join(',')}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) void onFileChange(field, file)
                    e.currentTarget.value = ''
                  }}
                />
              </label>
            )}
          </div>
          {fileState?.error && (
            <p className="text-sm text-destructive" role="alert">
              {fileState.error}
            </p>
          )}
          {errorNode}
        </div>
      )

    case 'mailing_list': {
      const consentLabel =
        field.placeholder?.trim() || 'Yes, please add me to the ODD mailing list.'
      const subText =
        field.description?.trim() || 'Would you like to join the One Day Dance mailing list?'
      return (
        <div className="space-y-2">
          {labelNode}
          <p className="text-xs text-muted-foreground">{subText}</p>
          <Controller
            name={fieldKey as `answers.${string}`}
            control={control}
            defaultValue={false}
            render={({ field: f }) => (
              <label className="flex cursor-pointer items-start gap-3 rounded-md border bg-muted/20 px-3 py-3">
                <Checkbox
                  checked={(f.value as boolean) ?? false}
                  onCheckedChange={f.onChange}
                  disabled={disabled}
                  className="mt-0.5"
                />
                <span className="text-sm">{consentLabel}</span>
              </label>
            )}
          />
          {errorNode}
        </div>
      )
    }

    case 'text_list':
      return (
        <div className="space-y-2">
          {labelNode}
          {descNode}
          <Controller
            name={fieldKey as `answers.${string}`}
            control={control}
            rules={
              field.required
                ? {
                    validate: (v) =>
                      (Array.isArray(v) && v.length > 0) || `${field.label} is required`,
                  }
                : {}
            }
            render={({ field: f }) => (
              <TextListInput
                id={field.id}
                value={Array.isArray(f.value) ? (f.value as string[]) : []}
                onChange={f.onChange}
                placeholder={field.placeholder}
                maxEntries={field.maxEntries}
                disabled={disabled}
              />
            )}
          />
          {errorNode}
        </div>
      )

    case 'payment': {
      const currency = (field.paymentCurrency ?? 'usd').toUpperCase()
      const tiers = field.paymentTiers ?? []
      const isTiered = tiers.length > 0
      return (
        <div className="space-y-2">
          {labelNode}
          {descNode}
          {field.paymentDescription && (
            <p className="text-sm text-muted-foreground">{field.paymentDescription}</p>
          )}
          {isTiered ? (
            <Controller
              name={fieldKey as `answers.${string}`}
              control={control}
              rules={field.required ? { required: `${field.label} is required` } : rules}
              render={({ field: f }) => (
                <div className="space-y-2">
                  {tiers.map((tier) => (
                    <label
                      key={tier.id}
                      className="flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 transition-colors hover:bg-muted/30"
                    >
                      <input
                        type="radio"
                        value={tier.id}
                        checked={f.value === tier.id}
                        onChange={() => f.onChange(tier.id)}
                        disabled={disabled}
                        className="mt-1 accent-primary"
                      />
                      <div className="flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-sm font-medium">{tier.label}</span>
                          <span className="text-sm font-semibold">
                            {formatCurrencyCents(tier.amountCents, currency)}
                          </span>
                        </div>
                        {tier.description && (
                          <p className="mt-0.5 text-xs text-muted-foreground">{tier.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            />
          ) : (
            <div className="rounded-md border bg-muted/20 px-3 py-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Submission fee</span>
                <span className="font-semibold">
                  {formatCurrencyCents(field.paymentAmountCents ?? 0, currency)}
                </span>
              </div>
            </div>
          )}
          {errorNode}
        </div>
      )
    }

    default:
      return null
  }
}

function formatCurrencyCents(amountCents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountCents / 100)
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency.toUpperCase()}`
  }
}
